/**
 * Client typé des edge functions de publication réseaux sociaux du blog.
 *
 * `social-oauth-start` (URL de consentement Meta/LinkedIn), `blog-carousel-render`
 * (rendu des slides), `blog-social-post` (publication 1-clic par réseau).
 * Calque le pattern `invokeBlog` de `blog-client.ts`.
 */

import { supabase } from '@/lib/supabase';

/** Réseaux adressables (boutons indépendants). */
export type SocialNetwork = 'linkedin' | 'facebook' | 'instagram';
/** Fournisseur OAuth : Meta couvre Facebook + Instagram ; LinkedIn, TikTok et Google séparés. */
export type SocialProvider = 'meta' | 'linkedin' | 'tiktok' | 'google';

export class SocialError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'SocialError';
  }
}

async function invokeSocial<T>(fn: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>(fn, { body });
  if (error) {
    let message = error.message;
    let code: string | undefined;
    const ctx = (error as Error & { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const parsed = (await ctx.json()) as { error?: string; message?: string };
        code = parsed.error;
        if (parsed.message) message = parsed.message;
        else if (parsed.error) message = parsed.error;
      } catch {
        /* garde le message générique */
      }
    }
    throw new SocialError(message, code);
  }
  if (!data) throw new SocialError('empty_response');
  return data;
}

export function startSocialOAuth(provider: SocialProvider): Promise<{ url: string }> {
  return invokeSocial('social-oauth-start', { provider });
}

export interface CarouselSlide {
  url: string;
  kind: 'cover' | 'text' | 'poll';
}

/** Plateformes ayant un carrousel d'images dédié (format propre à chacune). */
export type CarouselPlatform = 'instagram' | 'linkedin';

/**
 * Rend UN slide (indexé) du carrousel d'UNE plateforme (Instagram portrait 4:5 /
 * LinkedIn carré 1:1) et renvoie le set partiel + le total. UN slide par appel :
 * le rendu image (Satori + JPEG, WASM) est CPU-bound → empiler les slides dans
 * une seule invocation Edge dépasse le budget compute. Le client boucle
 * `slideIndex` de 0 à `total_slides-1` (cf. `renderBlogCarouselAll`).
 */
export function renderBlogCarousel(
  articleId: string,
  platform: CarouselPlatform,
  slideIndex: number,
): Promise<{
  ok: boolean;
  platform: CarouselPlatform;
  slide_index: number;
  total_slides: number;
  slide: CarouselSlide;
  slides: CarouselSlide[];
}> {
  return invokeSocial('blog-carousel-render', {
    article_id: articleId,
    platform,
    slide_index: slideIndex,
  });
}

/**
 * Rend TOUT le carrousel d'une plateforme en bouclant slide par slide (chaque
 * appel = 1 invocation Edge, son propre budget compute). `onProgress` est appelé
 * après chaque slide avec le set partiel pour un affichage incrémental.
 */
export async function renderBlogCarouselAll(
  articleId: string,
  platform: CarouselPlatform,
  onProgress?: (slides: CarouselSlide[], done: number, total: number) => void,
): Promise<CarouselSlide[]> {
  let total = 1; // borne réelle connue après le 1er appel
  let slides: CarouselSlide[] = [];
  for (let i = 0; i < total; i++) {
    const res = await renderBlogCarousel(articleId, platform, i);
    total = res.total_slides;
    slides = res.slides ?? [];
    onProgress?.(slides, i + 1, total);
  }
  return slides;
}

export function postBlogSocial(
  articleId: string,
  platform: SocialNetwork,
): Promise<{ ok: boolean; externalId?: string; externalUrl?: string }> {
  return invokeSocial('blog-social-post', { article_id: articleId, platform });
}

// ── Paramètres de connexion (social_app_configs, migration 0132) ─────────────

/** Ligne `social_app_configs` (valeurs publiques + flag secret, jamais le secret). */
export interface SocialAppConfig {
  provider: SocialProvider;
  client_id: string | null;
  page_id: string | null;
  org_id: string | null;
  api_version: string | null;
  redirect_uri: string | null;
  secret_set: boolean;
  updated_at: string | null;
}

/** Champs modifiables (le `secret` est write-only → Vault, jamais relu). */
export interface SocialConfigPatch {
  client_id?: string;
  page_id?: string;
  org_id?: string;
  api_version?: string;
  redirect_uri?: string;
  secret?: string;
}

export function updateSocialConfig(
  provider: SocialProvider,
  patch: SocialConfigPatch,
): Promise<{ ok: boolean; config: SocialAppConfig }> {
  return invokeSocial('social-config-update', { provider, ...patch });
}
